import { programmesQuery } from "@common/api/maintenance-programme"
import type { ProgrammeDetailPublic } from "@common/client"
import type { FilterFieldConfig } from "@common/components/filters/filter-config"
import type { Option } from "@common/components/shared/multi-select-body"
import { useQuery } from "@tanstack/react-query"
import { Factory, PlaneTakeoff, UserRound } from "lucide-react"
import { PROGRAMMES } from "./search"

export type ProgrammeFilterValues = {
  manufacturer?: string[]
  model?: string[]
  updater?: string[]
}

export const NO_PROGRAMME_FILTERS: ProgrammeFilterValues = {
  manufacturer: undefined,
  model: undefined,
  updater: undefined,
}

export function hasProgrammeFilters(values: ProgrammeFilterValues): boolean {
  return Boolean(
    values.manufacturer?.length ||
      values.model?.length ||
      values.updater?.length,
  )
}

export function matchesFilters(
  programme: ProgrammeDetailPublic,
  values: ProgrammeFilterValues,
): boolean {
  const within = (picked: string[] | undefined, value: string | undefined) =>
    !picked?.length || (value !== undefined && picked.includes(value))
  const models = programme.model_variants.map(
    (variant) => variant.marketing_name,
  )
  return (
    within(values.manufacturer, programme.manufacturer ?? undefined) &&
    (!values.model?.length ||
      models.some((model) => values.model?.includes(model))) &&
    within(values.updater, programme.last_modified_by?.id)
  )
}

function useProgrammeOptions(
  optionsOf: (programmes: ProgrammeDetailPublic[]) => Map<string, string>,
): { options: Option<string>[]; isLoading: boolean } {
  const { data, isLoading } = useQuery(programmesQuery(PROGRAMMES))
  const options = [...optionsOf(data?.data ?? [])]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label))
  return { options, isLoading }
}

function byManufacturer(
  programmes: ProgrammeDetailPublic[],
): Map<string, string> {
  return new Map(
    programmes.flatMap(({ manufacturer }) =>
      manufacturer ? [[manufacturer, manufacturer]] : [],
    ),
  )
}

function byModel(programmes: ProgrammeDetailPublic[]): Map<string, string> {
  return new Map(
    programmes.flatMap(({ model_variants }) =>
      model_variants.map(({ marketing_name }) => [
        marketing_name,
        marketing_name,
      ]),
    ),
  )
}

function byUpdater(programmes: ProgrammeDetailPublic[]): Map<string, string> {
  return new Map(
    programmes.flatMap(({ last_modified_by: user }) =>
      user ? [[user.id, `${user.first_name} ${user.last_name}`.trim()]] : [],
    ),
  )
}

export const programmeFilterConfigs: FilterFieldConfig<ProgrammeFilterValues>[] =
  [
    {
      key: "manufacturer",
      label: "Manufacturer",
      icon: Factory,
      type: "multiselect",
      searchPlaceholder: "Search manufacturers",
      emptyLabel: "No manufacturers",
      useOptions: () => useProgrammeOptions(byManufacturer),
      get: (values) => values.manufacturer,
      set: (next) => ({ manufacturer: next?.map(String) }),
    },
    {
      key: "model",
      label: "Model",
      icon: PlaneTakeoff,
      type: "multiselect",
      searchPlaceholder: "Search models",
      emptyLabel: "No models",
      useOptions: () => useProgrammeOptions(byModel),
      get: (values) => values.model,
      set: (next) => ({ model: next?.map(String) }),
    },
    {
      key: "updater",
      label: "Updated by",
      icon: UserRound,
      type: "multiselect",
      searchPlaceholder: "Search people",
      emptyLabel: "No people",
      useOptions: () => useProgrammeOptions(byUpdater),
      get: (values) => values.updater,
      set: (next) => ({ updater: next?.map(String) }),
    },
  ]
